import { useEffect, useState } from 'react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { api, shouldRetry } from '@/lib/api'
import type {
  BannedTagInput,
  DeletionDecision,
  DeletionStatus,
  ImageDetail,
  ImageItem,
  ImagePatch,
} from '@/lib/types'
import { lastToken } from '@/lib/tags'
import { dedupeCandidates } from '@/lib/admin'

export const keys = {
  feed: (q: string, unsafe: boolean) => ['images', 'feed', { q, unsafe }] as const,
  image: (id: string) => ['images', 'detail', id] as const,
  mine: ['images', 'mine'] as const,
  suggest: (q: string) => ['suggest', q] as const,
  aiProgress: ['admin', 'ai-progress'] as const,
  deletions: ['admin', 'deletions'] as const,
  deletionSummary: ['admin', 'deletions', 'summary'] as const,
  deletionList: (status: DeletionStatus, reason: string) => ['admin', 'deletions', 'list', { status, reason }] as const,
  bannedTags: ['admin', 'banned-tags'] as const,
}

function dedupe<T extends { id: string }>(pages: { images: T[] | null }[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const page of pages) {
    for (const item of page.images ?? []) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      out.push(item)
    }
  }
  return out
}

export function useImageFeed(q: string, unsafe: boolean) {
  const query = useInfiniteQuery({
    queryKey: keys.feed(q, unsafe),
    queryFn: ({ pageParam, signal }) => api.images({ q, cursor: pageParam || undefined, unsafe }, signal),
    initialPageParam: '',
    getNextPageParam: (last) => (last.nextId && last.images?.length ? last.nextId : undefined),
    staleTime: 60_000,
  })
  const images: ImageItem[] = query.data ? dedupe(query.data.pages) : []
  const count = query.data?.pages[0]?.count ?? 0
  return { ...query, images, count }
}

export function useMyUploads(enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: keys.mine,
    queryFn: ({ pageParam, signal }) => api.myUploads(pageParam || undefined, signal),
    initialPageParam: '',
    getNextPageParam: (last) => (last.nextId && last.images?.length ? last.nextId : undefined),
    enabled,
    // Keep polling while something is still being processed by the worker.
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.images?.some((i) => i.status === 'processing')) ? 8000 : false,
  })
  return { ...query, images: query.data ? dedupe(query.data.pages) : [] }
}

export function useImage(id: string | undefined) {
  return useQuery({
    queryKey: keys.image(id ?? ''),
    queryFn: ({ signal }) => api.image(id!, signal),
    enabled: !!id,
    // The original's URL is presigned for an hour.
    staleTime: 30 * 60_000,
    // 401 (sign in for non-safe images), 404 and 451 (blocked) are final.
    retry: (count, err) => shouldRetry(count, err, 2),
  })
}

export function useUpdateImage(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patch: ImagePatch) => api.updateImage(id, patch),
    onSuccess: (detail: ImageDetail) => {
      client.setQueryData(keys.image(id), detail)
      void client.invalidateQueries({ queryKey: ['images', 'feed'] })
      void client.invalidateQueries({ queryKey: keys.mine })
    },
  })
}

export function useDeleteImage(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.deleteImage(id),
    onSuccess: () => {
      client.removeQueries({ queryKey: keys.image(id) })
      void client.invalidateQueries({ queryKey: ['images', 'feed'] })
      void client.invalidateQueries({ queryKey: keys.mine })
    },
  })
}

export function useAiProgress(enabled: boolean) {
  return useQuery({
    queryKey: keys.aiProgress,
    queryFn: ({ signal }) => api.aiProgress(signal),
    enabled,
    refetchInterval: 15_000,
  })
}

export function useDeletionSummary(enabled: boolean) {
  return useQuery({
    queryKey: keys.deletionSummary,
    queryFn: ({ signal }) => api.deletionSummary(signal),
    enabled,
    // Follow a running cleanup until it is done.
    refetchInterval: (q) => (q.state.data?.cleanup_running ? 10_000 : false),
  })
}

export function useDeletions(status: DeletionStatus, reason: string, enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: keys.deletionList(status, reason),
    queryFn: ({ pageParam, signal }) =>
      api.deletions({ status, reason: reason || undefined, cursor: pageParam || undefined }, signal),
    initialPageParam: '',
    getNextPageParam: (last) => (last.next_cursor && last.items?.length ? last.next_cursor : undefined),
    enabled,
  })
  return { ...query, items: query.data ? dedupeCandidates(query.data.pages) : [] }
}

export function useDecideDeletions() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, decision }: { ids: string[]; decision: DeletionDecision }) =>
      api.decideDeletions(ids, decision),
    onSettled: () => client.invalidateQueries({ queryKey: keys.deletions }),
  })
}

export function useDecideAllDeletions() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ reason, decision }: { reason: string; decision: DeletionDecision }) =>
      api.decideAllDeletions(reason, decision),
    onSettled: () => client.invalidateQueries({ queryKey: keys.deletions }),
  })
}

export function useStartCleanup() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.startCleanup(),
    onSettled: () => client.invalidateQueries({ queryKey: keys.deletions }),
  })
}

export function useBannedTags(enabled: boolean) {
  return useQuery({
    queryKey: keys.bannedTags,
    queryFn: async ({ signal }) => (await api.bannedTags(signal)).items ?? [],
    enabled,
  })
}

function invalidateAfterBan(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: keys.bannedTags })
  // What is visible in the feed, search and uploads changes with the ban list.
  void client.invalidateQueries({ queryKey: ['images'] })
}

export function useBanTag() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: BannedTagInput) => api.banTag(input),
    onSettled: () => invalidateAfterBan(client),
  })
}

export function useUnbanTag() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (tag: string) => api.unbanTag(tag),
    onSettled: () => invalidateAfterBan(client),
  })
}

export function useDebounced<T>(value: T, delay = 180): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

/** Tag suggestions for the word currently being typed. */
export function useSuggestions(input: string) {
  const token = useDebounced(lastToken(input).trim().toLowerCase())
  return useQuery({
    queryKey: keys.suggest(token),
    queryFn: async ({ signal }) => (await api.suggest(token, signal)) ?? [],
    enabled: token.length >= 2,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    select: (list) => list.slice(0, 8),
  })
}

